import { useCallback, useEffect, useState } from 'react';
import { useLocation, useSearchParams } from 'react-router-dom';

export type ManageListFilter = 'attention_needed' | 'all';

function readFilterFromParams(p: URLSearchParams): ManageListFilter {
  const v = p.get('attention_needed');
  if (v === 'true' || v === '1') return 'attention_needed';
  return 'all';
}

/**
 * Keeps accounts/campaigns toolbar filter + search in sync with the URL
 * (`attention_needed`, `search`) for deep links and refresh/share.
 */
export function useManageListUrlParams() {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<ManageListFilter>(() => readFilterFromParams(searchParams));
  const [searchQuery, setSearchQuery] = useState(() => searchParams.get('search') || '');

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    setFilter(readFilterFromParams(params));
    setSearchQuery(params.get('search') || '');
  }, [location.search]);

  const setFilterWithUrl = useCallback(
    (next: ManageListFilter) => {
      setFilter(next);
      const nextParams = new URLSearchParams(searchParams);
      if (next === 'attention_needed') {
        nextParams.set('attention_needed', 'true');
      } else {
        nextParams.delete('attention_needed');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  const setSearchWithUrl = useCallback(
    (value: string) => {
      setSearchQuery(value);
      const nextParams = new URLSearchParams(searchParams);
      const t = value.trim();
      if (t) {
        nextParams.set('search', t);
      } else {
        nextParams.delete('search');
      }
      setSearchParams(nextParams, { replace: true });
    },
    [searchParams, setSearchParams]
  );

  return {
    filter,
    searchQuery,
    setFilterWithUrl,
    setSearchWithUrl,
  };
}
